import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { CRMOrderApiAdapter } from '../adapters/CRMOrderApiAdapter';
import { CRMApiAdapter } from '../../List/adapters/CRMApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import type { CRMOrder } from '../models/CRMOrder';

export const useCRMOrder = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<CRMOrder | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const isInitialLoadRef = useRef<boolean>(true);

  const fetchOrder = useCallback(async (showLoader = true) => {
    if (!orderId) {
      setError('No se ha proporcionado un ID de pedido.');
      setLoading(false);
      return;
    }

    const shouldShowLoader = showLoader && isInitialLoadRef.current;

    try {
      if (shouldShowLoader) {
        setLoading(true);
      }
      
      const httpClient = createHttpClient(apiClientWH);
      const crmApiAdapter = new CRMApiAdapter(httpClient);
      const crmOrderApiAdapter = new CRMOrderApiAdapter(httpClient, crmApiAdapter);

      // Obtener pedido completo con datos de Orders y CRM
      const crmOrder = await crmOrderApiAdapter.getCRMOrderById(Number(orderId));

      setOrder(crmOrder);
      setError(null);
      
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }
    } catch (err: any) {
      setError(err.message || 'Error al cargar el pedido');
    } finally {
      if (shouldShowLoader) {
        setLoading(false);
      }
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder(true);
  }, [fetchOrder]);

  const refetch = useCallback(() => {
    fetchOrder(false);
  }, [fetchOrder]);

  // Función para actualizar el order de forma optimista
  const updateOrder = useCallback((updater: (prev: CRMOrder | null) => CRMOrder | null) => {
    setOrder((prev) => updater(prev));
  }, []);

  return {
    order,
    loading,
    error,
    refetch,
    updateOrder,
  };
};
